import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Dialog } from '@/components/ui/Dialog';
import { Segmented } from '@/components/ui/Segmented';
import { Textarea } from '@/components/ui/Textarea';
import {
  useAdminLocales,
  useI18nSync,
  useCreateLocale,
  useUpdateLocale,
} from '@/features/i18n/api';
import type { CreateLocaleInput, LocaleCoverage } from '@honeycomb/shared';

export const Route = createFileRoute('/_authed/i18n/')({
  component: I18nLocalesPage,
});

function ErrorBox({ message }: { message: string }) {
  return (
    <div
      className="text-xs rounded-lg p-3"
      style={{
        background: 'var(--color-error-bg)',
        border: '1px solid var(--color-error-border)',
        color: 'var(--color-error-text)',
      }}
    >
      {message}
    </div>
  );
}

function CoverageBar({ translated, total }: { translated: number; total: number }) {
  const pct = total === 0 ? 0 : Math.round((translated / total) * 100);
  return (
    <div className="flex items-center gap-2 min-w-[140px]">
      <div className="flex-1 h-2 rounded-full bg-cream-deep overflow-hidden">
        <div
          className="h-full rounded-full"
          style={{ width: `${pct}%`, background: 'var(--color-honey)' }}
        />
      </div>
      <span className="font-mono text-xs tabular-nums text-ink-soft whitespace-nowrap">
        {translated}/{total}
      </span>
    </div>
  );
}

function AddLocaleDialog({ onClose }: { onClose: () => void }) {
  const create = useCreateLocale();
  const [tag, setTag] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [measurementSystem, setMeasurementSystem] = useState<'metric' | 'imperial'>('metric');
  const [temperatureScale, setTemperatureScale] = useState<'C' | 'F'>('C');
  const [promptNotes, setPromptNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    setError(null);
    const body: CreateLocaleInput = {
      tag: tag.trim(),
      displayName: displayName.trim(),
      measurementSystem,
      temperatureScale,
      promptNotes: promptNotes.trim() || null,
    };
    try {
      await create.mutateAsync(body);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create locale');
    }
  };

  const canSubmit = tag.trim().length > 0 && displayName.trim().length > 0 && !create.isPending;

  return (
    <Dialog open onClose={onClose} title="Add locale" testId="i18n-add-locale-dialog">
      <div className="flex flex-col gap-4">
        <Input
          label="Locale tag"
          placeholder="e.g. de-DE"
          value={tag}
          onChange={(e) => setTag(e.target.value)}
          data-testid="i18n-locale-tag-input"
          hint="BCP-47 tag, e.g. en-US, de-DE, fr-FR."
        />
        <Input
          label="Display name"
          placeholder="e.g. German (Germany)"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          data-testid="i18n-locale-name-input"
        />
        <div className="flex flex-col gap-1.5">
          <div className="text-sm text-ink-soft">Measurement system</div>
          <Segmented
            value={measurementSystem}
            onChange={setMeasurementSystem}
            options={[
              { value: 'metric', label: 'Metric' },
              { value: 'imperial', label: 'Imperial' },
            ]}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <div className="text-sm text-ink-soft">Temperature scale</div>
          <Segmented
            value={temperatureScale}
            onChange={setTemperatureScale}
            options={[
              { value: 'C', label: 'Celsius' },
              { value: 'F', label: 'Fahrenheit' },
            ]}
          />
        </div>
        <Textarea
          label="Prompt notes (optional)"
          placeholder="Locale conventions fed to the LLM [LOCALE] block."
          value={promptNotes}
          onChange={(e) => setPromptNotes(e.target.value)}
          data-testid="i18n-locale-notes-input"
        />
        {error && <ErrorBox message={error} />}
        <div className="flex justify-end gap-2 mt-2">
          <Button variant="ghost" onClick={onClose} disabled={create.isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!canSubmit}
            data-testid="i18n-create-locale-submit"
          >
            {create.isPending ? 'Creating…' : 'Add locale'}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

function I18nLocalesPage() {
  const { data: locales, isLoading, isError, error } = useAdminLocales();
  const sync = useI18nSync();
  const updateLocale = useUpdateLocale();
  const [addOpen, setAddOpen] = useState(false);

  const totalKeys = sync.data?.registeredKeys ?? 0;
  const coverageByTag = new Map<string, LocaleCoverage>(
    (sync.data?.locales ?? []).map((l) => [l.tag, l]),
  );

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="i18n-page">
      <header className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl leading-tight">Localization</h1>
          <p className="text-ink-soft text-sm mt-1">
            {totalKeys} registered message key{totalKeys === 1 ? '' : 's'}.
          </p>
        </div>
        <Button
          variant="primary"
          size="sm"
          data-testid="i18n-add-locale-button"
          onClick={() => setAddOpen(true)}
        >
          Add locale
        </Button>
      </header>

      <Card shadow="sm" className="p-0 overflow-hidden">
        {isLoading ? (
          <div className="p-6 text-ink-soft">Loading…</div>
        ) : isError ? (
          <div className="p-6 text-ink-soft">
            Failed to load locales{error instanceof Error ? `: ${error.message}` : '.'}
          </div>
        ) : (locales ?? []).length === 0 ? (
          <div className="p-6 text-ink-faint" data-testid="i18n-empty">
            No locales yet. Add one to start translating.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr
                className="text-left text-xs uppercase tracking-wide text-ink-faint border-b"
                style={{ borderColor: 'var(--color-hairline)' }}
              >
                <th className="px-4 py-3">Tag</th>
                <th className="px-4 py-3">Display name</th>
                <th className="px-4 py-3">Coverage</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 w-24"></th>
              </tr>
            </thead>
            <tbody>
              {(locales ?? []).map((l) => {
                const cov = coverageByTag.get(l.tag);
                const archived = Boolean(l.archivedAt);
                return (
                  <tr
                    key={l.tag}
                    className="border-b last:border-b-0"
                    style={{ borderColor: 'var(--color-hairline)' }}
                    data-testid={`i18n-locale-row-${l.tag}`}
                  >
                    <td className="px-4 py-3">
                      <Link
                        to="/i18n/$tag"
                        params={{ tag: l.tag }}
                        className="font-mono text-xs text-honey-deep hover:underline"
                        data-testid={`i18n-locale-link-${l.tag}`}
                      >
                        {l.tag}
                      </Link>
                    </td>
                    <td className="px-4 py-3">{l.displayName}</td>
                    <td className="px-4 py-3" data-testid={`i18n-locale-coverage-${l.tag}`}>
                      <CoverageBar translated={cov?.translated ?? 0} total={totalKeys} />
                    </td>
                    <td className="px-4 py-3">
                      {archived ? (
                        <Badge tone="neutral">Archived</Badge>
                      ) : l.enabled ? (
                        <Badge tone="sage" data-testid={`i18n-locale-status-${l.tag}`}>
                          Enabled
                        </Badge>
                      ) : (
                        <Badge tone="honey" data-testid={`i18n-locale-status-${l.tag}`}>
                          Disabled
                        </Badge>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {!archived && (
                        <Button
                          variant="ghost"
                          size="sm"
                          data-testid={`i18n-locale-enabled-toggle-${l.tag}`}
                          disabled={updateLocale.isPending}
                          onClick={() =>
                            updateLocale.mutate({ tag: l.tag, body: { enabled: !l.enabled } })
                          }
                        >
                          {l.enabled ? 'Disable' : 'Enable'}
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>

      {addOpen && <AddLocaleDialog onClose={() => setAddOpen(false)} />}
    </div>
  );
}
